import { TypeMyAccountPageTilesFields } from '@/core/generated-types';
import React, { FC } from 'react';
import useLocale from '@/hooks/useLocale';
import { Errors, Loader } from '@/components';
import { usePageData } from '@/context/PageDataProvider';
import { TypeMyAccountData } from '@/pages/[locale]/account';
import { useErrorDirectives } from '@/context/ErrorDirectiveContext';
import { findApplicableErrorDetails } from '@/components/common/Errors/utils';
import { ERROR_KINDS } from '@/core/constants';

import {
  PlayKitsTile,
  SubscriberShopTile,
  ReferAFriendTile,
  CoursePacksTile,
  OrderHistoryTile,
  AccountSettingsTile,
  ReadingSkillSetTile,
  ERCSubscriptionTile,
} from './components';

import generateTranslationsObject from '@/utils/translations/generateTranslationObject';
import { TypeAccountSettingsPageTranslations } from '@/utils/translations/types/TypeAccountSettingsPageTranslations';
import useSubscriptions from '@/lib/api/subscriptionManager/hooks/useSubscriptions';
import useSubscriptionsCount from '@/lib/api/subscriptionManager/hooks/useSubscriptionsCount';
import {
  SUBSCRIPTION_STATUS,
  TypeSubscriptionCountData,
} from '@/lib/api/subscriptionManager/types';

const MyAccountPageTiles: FC<TypeMyAccountPageTilesFields> = ({
  playKitsTile,
  subscriberShopTile,
  referAFriendTile,
  coursePacksTile,
  orderHistoryTile,
  accountSettingsTile,
  readingSkillSetTile,
  earlyReaderClubTile,
  translations,
}) => {
  const locale = useLocale();
  const { errorDirectives } = useErrorDirectives();
  const { errors, heading } = findApplicableErrorDetails(
    errorDirectives,
    ERROR_KINDS.DEFAULT,
  );

  const pageData = usePageData() as TypeMyAccountData;
  const { user, hasCourses, hasSkillSets, hasERCSubscriptions } = {
    ...pageData,
  };

  // prefetch subscriptions in background to support performance loading sub shop page
  useSubscriptions({ userUuid: user?.uuid });

  const {
    data: subscriptionCounts,
    isLoading: subscriptionCountsLoading,
    error: subscriptionCountsError,
  } = useSubscriptionsCount({
    userUuid: user?.uuid,
    statuses: [
      SUBSCRIPTION_STATUS.ACTIVE,
      SUBSCRIPTION_STATUS.CANCELLED,
      SUBSCRIPTION_STATUS.FINISHED,
    ],
  });

  if (subscriptionCountsLoading) {
    return <Loader loading />;
  }

  if (!_isPageDataValid(pageData) || subscriptionCountsError) {
    return <Errors errors={errors} heading={heading} />;
  }

  const t = generateTranslationsObject(
    translations.fields,
  ) as TypeAccountSettingsPageTranslations;

  const subscriptionCount = _getTotalSubscriptionCount(subscriptionCounts);

  return (
    <div className="container mx-auto mb-32 mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="flex flex-col gap-6">
        <PlayKitsTile t={t} tile={playKitsTile.fields} />
        <SubscriberShopTile
          tile={subscriberShopTile.fields}
          hasValidSubscriptions={subscriptionCount > 0}
        />
        {/* Desktop Referral tile */}
        <ReferAFriendTile
          t={t}
          userUuid={user.uuid}
          tile={referAFriendTile.fields}
          locale={locale}
          classes="hidden lg:block"
        />
      </div>
      <div className="flex flex-col gap-6">
        {hasERCSubscriptions && (
          <ERCSubscriptionTile tile={earlyReaderClubTile?.fields} />
        )}
        <ReadingSkillSetTile
          t={t}
          tile={readingSkillSetTile.fields}
          hasSkillSets={hasSkillSets}
          locale={locale}
        />
        <CoursePacksTile
          tile={coursePacksTile.fields}
          hasCourses={hasCourses}
        />
        <OrderHistoryTile
          t={t}
          userUuid={user.uuid}
          tile={orderHistoryTile.fields}
          locale={locale}
        />
        <AccountSettingsTile tile={accountSettingsTile.fields} />
        {/* Mobile Referral tile */}
        <ReferAFriendTile
          t={t}
          userUuid={user.uuid}
          tile={referAFriendTile.fields}
          locale={locale}
          classes="lg:hidden"
        />
      </div>
    </div>
  );
};

export function _getTotalSubscriptionCount(data?: TypeSubscriptionCountData) {
  let subscriptionsCount = 0;

  if (data?.active) {
    subscriptionsCount += data.active;
  }

  if (data?.cancelled) {
    subscriptionsCount += data.cancelled;
  }

  if (data?.finished) {
    subscriptionsCount += data.finished;
  }

  return subscriptionsCount;
}

export function _isPageDataValid(pageData: TypeMyAccountData) {
  // The user is the only data type that should be consistently available across all page states.
  // To facilitate null checks we will do this before rendering any of the page tiles so the tiles can
  // depend on this data being there. The tiles should support null checks for all other dependent data.
  return !!pageData?.user;
}

export default MyAccountPageTiles;
